import type {
  InspectionResult,
  MeshHealthDiagnosis,
  PrintabilityAssessment,
} from "@voxelspy/analysis";
import type {
  ContractWarning,
  GeometryProvenance,
  NormalizedModel,
  Vec3,
} from "@voxelspy/contracts";
import type { SupportedFormat } from "@voxelspy/importers";
import {
  requireSupportedFormat,
  resolveFrameOptions,
  type FrameSource,
  type ResolvedSourceAxis,
  type ResolvedSourceUnit,
} from "./formats";

type ResolvedUnit = ResolvedSourceUnit;
type ResolvedAxis = ResolvedSourceAxis;

export interface InspectSource {
  file: File;
  unit: ResolvedUnit | "";
  axis: ResolvedAxis | "";
  frameSource?: FrameSource;
}

/**
 * Message protocol for the dedicated inspection worker (`inspect.worker.ts`).
 * This is an app-internal channel, deliberately outside the versioned
 * comparison-worker protocol in `@voxelspy/contracts`: inspecting a single
 * model needs neither the multi-operation session state nor the
 * cancellation/progress handshake `comparison.worker.ts` implements for a
 * potentially long-running two-model analysis. Both `inspectModel` and
 * `diagnoseMeshHealth` are single bounded passes over one model's
 * already-imported geometry (see `packages/analysis/src/inspect.ts` and
 * `diagnose.ts`), so one request/response round trip per call is enough --
 * mirroring `summary-worker-client.ts`'s simpler protocol, not
 * `worker-client.ts`'s stateful one.
 *
 * `kind` distinguishes the cheap always-on report (`"inspect"`) from the
 * heavier opt-in mesh-health evidence (`"diagnose"`), the file Forensics
 * report (`"forensics"`, added for `/tools/file-forensics/`), and the
 * printability assessment (`"printability"`, added for
 * `/tools/printability/`); all four re-run the same local import from the
 * same source bytes, since a fresh dedicated worker is spun up and torn down
 * per call (see `runInspectWorker`/`runPrintabilityWorker` below) rather than
 * keeping an imported model alive across requests -- the file is small
 * (bounded by the shared 32 MiB / 500,000-triangle importer ceiling every
 * call applies), so re-importing costs far less than the complexity of a
 * stateful worker just to avoid it. This also means adjusting a printability
 * parameter (thin-wall threshold, overhang angle, build direction, or build
 * volume) and re-running is itself just another `"printability"` request,
 * exactly like `diagnoseModelAsync`'s "opt-in, re-run on demand" shape.
 * `"forensics"` asks the same importer the same question `"inspect"` does --
 * it never runs a second, different importer or validator -- and reports the
 * normalized model's own provenance, warnings, and mesh/instance structure
 * instead of `inspectModel`'s geometric measurements. `"printability"` runs
 * `assessPrintability` (`@voxelspy/analysis`), a single bounded pass over one
 * already-imported model like `"inspect"`/`"diagnose"`/`"forensics"` -- not
 * an interactive multi-query session like `measure-worker-client.ts`'s or
 * `clearance-worker-client.ts`'s dedicated channels, which exist because a
 * user issues many independent queries (snap points, sections, placements)
 * against one loaded model without re-importing it each time. Printability
 * has no such per-query shape: one call in, one bounded report out, so it
 * fits this file's kind-discriminated, fresh-worker-per-call pattern instead
 * of warranting a fourth worker file.
 */
export interface InspectWorkerRequestBase {
  readonly requestId: number;
  readonly format: SupportedFormat;
  readonly sourceName: string;
  readonly bytes: Uint8Array;
  readonly options: {
    declaredUnit?: ResolvedUnit;
    declaredAxis?: ResolvedAxis;
    userUnit?: ResolvedUnit;
    userAxis?: ResolvedAxis;
  };
}

/**
 * Wire shape for a `"printability"` request's assessment parameters --
 * mirrors `AssessPrintabilityOptions`'s `wallThickness`/`overhang`/
 * `buildVolume` leaves (`@voxelspy/analysis`), flattened to the handful of
 * fields `/tools/printability/`'s form actually exposes rather than the full
 * options shape (no `maxSampleTriangles`/`maxFindings`/`maxRegions`/
 * `maxComponents`/`executionBudget` overrides -- this release uses
 * `assessPrintability`'s own defaults and ceilings for those). Every field is
 * optional and `undefined` is passed straight through to
 * `assessPrintability`, which resolves its own documented default for each
 * (see `printability.ts`): omitting a field here is "use the engine's
 * default", never a client-side default duplicated in this file.
 */
export interface PrintabilityAssessmentRequestOptions {
  readonly thinThresholdMillimetres?: number;
  readonly overhangThresholdDegreesFromVertical?: number;
  readonly buildDirection?: Vec3;
  readonly buildVolumeDimensionsMillimetres?: Vec3;
}

export type InspectWorkerRequest =
  | (InspectWorkerRequestBase & { readonly kind: "inspect" })
  | (InspectWorkerRequestBase & { readonly kind: "diagnose" })
  | (InspectWorkerRequestBase & { readonly kind: "forensics" })
  | (InspectWorkerRequestBase & {
      readonly kind: "printability";
      // Optional (rather than required) purely so `runInspectWorker`'s
      // existing generic request builder below -- shared by
      // `"inspect"`/`"diagnose"`/`"forensics"`, none of which set this field
      // -- stays structurally valid for every kind without itself needing to
      // branch on `"printability"`. `runPrintabilityWorker` (the dedicated
      // client function for this kind, below) always supplies it explicitly.
      readonly assessmentOptions?: PrintabilityAssessmentRequestOptions;
    });

/**
 * What one successful inspection hands back to the UI: the serializable
 * `InspectionResult` plus the imported model's own `warnings` (import-time
 * notices such as unsupported-content approximations), which
 * `InspectionResult` does not carry itself -- it echoes only `frame` and
 * `provenance` from the source `NormalizedModel`, not `warnings`. Neither
 * field holds a typed-array geometry buffer: the worker imports the model,
 * computes the report, and lets the model (with its Float64Array positions
 * and Uint32Array indices) go out of scope inside the worker. No geometry
 * buffer is ever posted back to the main thread or held in React state.
 */
export interface InspectionOutcome {
  readonly inspection: InspectionResult;
  readonly warnings: readonly ContractWarning[];
}

/**
 * What one successful `"diagnose"` call hands back: the bounded
 * `MeshHealthDiagnosis` evidence plus the imported `NormalizedModel` itself.
 * Unlike `InspectionOutcome`, this *does* carry the model's typed-array
 * geometry back to the main thread -- deliberately, and only for this
 * opt-in, user-initiated call: `MeshHealthViewer` needs the model's own mesh
 * geometry to draw alongside the diagnostic overlay, the same way
 * `ComparisonFlow` already hands `Workbench` full baseline/candidate
 * `NormalizedModel`s for rendering. The model is bounded by the same
 * importer ceiling as every other call in this file, and it is held only in
 * the diagnostic viewer's own state for as long as that panel stays open.
 */
export interface MeshHealthDiagnosisOutcome {
  readonly model: NormalizedModel;
  readonly diagnosis: MeshHealthDiagnosis;
}

/** One imported mesh's placed structure, for the file Forensics report --
 *  counts derived from the mesh's own typed-array buffers inside the worker,
 *  never the buffers themselves. */
export interface ForensicsMeshSummary {
  readonly meshId: string;
  readonly vertexCount: number;
  readonly triangleCount: number;
}

/**
 * One placed instance of a mesh, for the file Forensics report. `transform`
 * is the exact 16-value column-major affine matrix the model placement
 * schema stores (`meshToModel` for today's `"flat"` placement, or
 * `meshToNode` for a future `"hierarchy"` placement -- see
 * `modelPlacementSchema` in `@voxelspy/contracts`); `transformKind` records
 * which one it is so the report never mislabels the matrix it shows.
 */
export interface ForensicsInstanceSummary {
  readonly instanceId: string;
  readonly meshId: string;
  readonly transformKind: "meshToModel" | "meshToNode";
  readonly transform: readonly number[];
}

/**
 * What one successful `"forensics"` call hands back: the imported model's
 * own `provenance` and `warnings` verbatim (nothing paraphrased or
 * recomputed), plus a placement-structure summary built from the model's
 * mesh/instance records. Deliberately excludes the model's geometry buffers
 * (`positions`/`indices`) -- Forensics reports structure and provenance, not
 * measurements, and never needs them on the main thread.
 */
export interface ForensicsOutcome {
  readonly provenance: GeometryProvenance;
  readonly warnings: readonly ContractWarning[];
  readonly placementKind: "flat" | "hierarchy";
  readonly meshes: readonly ForensicsMeshSummary[];
  readonly instances: readonly ForensicsInstanceSummary[];
}

/**
 * What one successful `"printability"` call hands back: the bounded
 * `PrintabilityAssessment` evidence plus the imported `NormalizedModel`
 * itself. Carries the model's typed-array geometry back to the main thread
 * for the same reason `MeshHealthDiagnosisOutcome` does -- `PrintabilityViewer`
 * needs the model's own mesh geometry (and `flattenedTriangleLocator` needs
 * the full `NormalizedModel` to resolve reported triangle indices back to
 * drawable positions) to draw the 3D evidence overlay alongside the textual
 * report.
 */
export interface PrintabilityOutcome {
  readonly model: NormalizedModel;
  readonly assessment: PrintabilityAssessment;
}

export type InspectWorkerResponse =
  | {
      readonly requestId: number;
      readonly kind: "inspect";
      readonly ok: true;
      readonly outcome: InspectionOutcome;
    }
  | {
      readonly requestId: number;
      readonly kind: "inspect";
      readonly ok: false;
      readonly message: string;
    }
  | {
      readonly requestId: number;
      readonly kind: "diagnose";
      readonly ok: true;
      readonly outcome: MeshHealthDiagnosisOutcome;
    }
  | {
      readonly requestId: number;
      readonly kind: "diagnose";
      readonly ok: false;
      readonly message: string;
    }
  | {
      readonly requestId: number;
      readonly kind: "forensics";
      readonly ok: true;
      readonly outcome: ForensicsOutcome;
    }
  | {
      readonly requestId: number;
      readonly kind: "forensics";
      readonly ok: false;
      readonly message: string;
    }
  | {
      readonly requestId: number;
      readonly kind: "printability";
      readonly ok: true;
      readonly outcome: PrintabilityOutcome;
    }
  | {
      readonly requestId: number;
      readonly kind: "printability";
      readonly ok: false;
      readonly message: string;
    };

/**
 * Raised when an inspection run is stopped because its `AbortSignal` fired,
 * distinct from a genuine import/inspection failure -- mirrors
 * `ComparisonCancelledError` in `worker-client.ts`.
 */
export class InspectionCancelledError extends Error {
  constructor(message = "Inspection cancelled.") {
    super(message);
    this.name = "InspectionCancelledError";
  }
}

/**
 * Runs one request/response round trip against a fresh dedicated inspection
 * worker: imports the source file and either inspects or diagnoses it,
 * entirely off the main thread. A fresh worker is spun up per call and torn
 * down once a result, failure, or abort settles the returned promise -- the
 * same lifecycle `summarizeModelComparisonAsync` uses, chosen for the same
 * reason: each call is a single bounded computation, not a session that
 * benefits from a persisted worker across multiple requests.
 *
 * The file's bytes are read once here and then transferred (not cloned) to
 * the worker: `File.arrayBuffer()` can be called again on a later,
 * independent call (e.g. `diagnoseModelAsync` after `inspectSourceAsync` for
 * the same source), since it does not consume the `File` itself -- only the
 * one `ArrayBuffer` produced for *this* call is transferred away.
 *
 * No network transmission is involved; the worker runs entirely in this
 * browser, and only the serializable response -- never a geometry buffer for
 * `"inspect"`, and only the one already-bounded model for `"diagnose"` --
 * travels back to the caller.
 */
async function runInspectWorker<K extends InspectWorkerRequest["kind"]>(
  source: InspectSource,
  kind: K,
  signal?: AbortSignal,
): Promise<Extract<InspectWorkerResponse, { kind: K }>> {
  const format = requireSupportedFormat(source.file.name);
  const bytes = new Uint8Array(await source.file.arrayBuffer());
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new InspectionCancelledError());
      return;
    }
    const worker = new Worker(new URL("./inspect.worker.ts", import.meta.url), {
      type: "module",
      name: `voxelspy-inspect-${kind}`,
    });
    const requestId = 1;
    let settled = false;

    const finish = (action: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      worker.terminate();
      action();
    };
    const onAbort = () => {
      finish(() => reject(new InspectionCancelledError()));
    };
    const onMessage = (event: MessageEvent<InspectWorkerResponse>) => {
      const data = event.data;
      if (data.requestId !== requestId || data.kind !== kind) return;
      finish(() =>
        resolve(data as Extract<InspectWorkerResponse, { kind: K }>),
      );
    };
    const onError = () => {
      finish(() =>
        reject(new Error("Inspection worker stopped unexpectedly.")),
      );
    };

    signal?.addEventListener("abort", onAbort);
    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    const request: InspectWorkerRequest = {
      kind,
      requestId,
      format,
      sourceName: source.file.name,
      bytes,
      options: resolveFrameOptions(
        format,
        source.frameSource ?? "default",
        source.unit,
        source.axis,
      ),
    };
    worker.postMessage(request, [request.bytes.buffer]);
  });
}

/** Imports one local model file and inspects it, entirely off the main
 * thread. See `runInspectWorker`'s doc comment for the shared worker
 * lifecycle. */
export async function inspectSourceAsync(
  source: InspectSource,
  signal?: AbortSignal,
): Promise<InspectionOutcome> {
  const response = await runInspectWorker(source, "inspect", signal);
  if (!response.ok) throw new Error(response.message);
  return response.outcome;
}

/**
 * Imports the same local model file again and runs the heavier, opt-in
 * `diagnoseMeshHealth` pass, entirely off the main thread. Deliberately not
 * run automatically alongside `inspectSourceAsync`: a caller invokes this
 * only when a user actually opens a diagnostic, matching
 * `diagnoseMeshHealth`'s own "opt-in heavier sibling of `inspectModel`"
 * design. See `runInspectWorker`'s doc comment for the shared worker
 * lifecycle and why re-importing here (rather than keeping the first
 * inspection's worker and model alive) is the simpler, still-cheap choice.
 */
export async function diagnoseModelAsync(
  source: InspectSource,
  signal?: AbortSignal,
): Promise<MeshHealthDiagnosisOutcome> {
  const response = await runInspectWorker(source, "diagnose", signal);
  if (!response.ok) throw new Error(response.message);
  return response.outcome;
}

/**
 * Imports one local model file and reports its file Forensics outcome
 * (provenance, warnings, and mesh/instance structure), entirely off the main
 * thread. Used by `/tools/file-forensics/`, distinct from
 * `inspectSourceAsync`'s geometric measurements. See `runInspectWorker`'s doc
 * comment for the shared worker lifecycle.
 */
export async function forensicsSourceAsync(
  source: InspectSource,
  signal?: AbortSignal,
): Promise<ForensicsOutcome> {
  const response = await runInspectWorker(source, "forensics", signal);
  if (!response.ok) throw new Error(response.message);
  return response.outcome;
}

/**
 * Runs one `"printability"` request/response round trip against a fresh
 * dedicated inspection worker (`inspect.worker.ts`, the same worker file
 * `runInspectWorker` above dispatches to). Written directly rather than
 * through `runInspectWorker`'s shared generic: every other kind's request is
 * fully determined by `InspectSource` alone, but a printability request also
 * carries `assessmentOptions` (thin-wall threshold, overhang angle, build
 * direction, build volume) -- a caller-supplied parameter set with no
 * equivalent on `"inspect"`/`"diagnose"`/`"forensics"`. Duplicating this
 * worker's launch/message/abort/teardown lifecycle here (rather than
 * threading an optional parameter through the shared generic) keeps that
 * generic's signature untouched for its three existing callers and keeps
 * this one printability-specific concern in one place, matching this file's
 * own established precedent of small, deliberate duplication over a shared
 * abstraction reshaped to fit one more case (see e.g. `ModelSourceCard`'s
 * duplication across `InspectFlow.tsx`/`ClearanceFlow.tsx`/
 * `MeasureSectionFlow.tsx`).
 */
async function runPrintabilityWorker(
  source: InspectSource,
  assessmentOptions: PrintabilityAssessmentRequestOptions,
  signal?: AbortSignal,
): Promise<Extract<InspectWorkerResponse, { kind: "printability" }>> {
  const format = requireSupportedFormat(source.file.name);
  const bytes = new Uint8Array(await source.file.arrayBuffer());
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new InspectionCancelledError());
      return;
    }
    const worker = new Worker(new URL("./inspect.worker.ts", import.meta.url), {
      type: "module",
      name: "voxelspy-inspect-printability",
    });
    const requestId = 1;
    let settled = false;

    const finish = (action: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      worker.terminate();
      action();
    };
    const onAbort = () => {
      finish(() => reject(new InspectionCancelledError()));
    };
    const onMessage = (event: MessageEvent<InspectWorkerResponse>) => {
      const data = event.data;
      if (data.requestId !== requestId || data.kind !== "printability") return;
      finish(() => resolve(data));
    };
    const onError = () => {
      finish(() =>
        reject(new Error("Inspection worker stopped unexpectedly.")),
      );
    };

    signal?.addEventListener("abort", onAbort);
    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    const request: InspectWorkerRequest = {
      kind: "printability",
      requestId,
      format,
      sourceName: source.file.name,
      bytes,
      options: resolveFrameOptions(
        format,
        source.frameSource ?? "default",
        source.unit,
        source.axis,
      ),
      assessmentOptions,
    };
    worker.postMessage(request, [request.bytes.buffer]);
  });
}

/**
 * Imports one local model file and runs `assessPrintability` against it,
 * entirely off the main thread, for `/tools/printability/`. Re-runnable with
 * different `assessmentOptions` against the same file (the caller keeps its
 * own `File` reference and calls this again) exactly the way
 * `diagnoseModelAsync` is re-run on demand -- see `runPrintabilityWorker`'s
 * doc comment for the worker lifecycle.
 */
export async function assessPrintabilitySourceAsync(
  source: InspectSource,
  assessmentOptions: PrintabilityAssessmentRequestOptions,
  signal?: AbortSignal,
): Promise<PrintabilityOutcome> {
  const response = await runPrintabilityWorker(
    source,
    assessmentOptions,
    signal,
  );
  if (!response.ok) throw new Error(response.message);
  return response.outcome;
}
